import React, { FC } from 'react';
import { View } from 'react-native';
import OperatorPanelButton from '@components/operatorPanel/components/elements/buttons/OperatorPanelButton';
import { scale as initialScale } from '@components/frame';
import { connect } from 'react-redux';
import { ncFileStatuses } from '@helpers';
import { useOperatorPanelStatuses } from '@components/operatorPanel/hooks/useOperatorPanelStatuses';
import { font as initialFont } from '@styles/fonts';
import HomePageImages from '@components/operatorPanel/components/HomePage/HomePageImages';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useManageHomePageButtonsPress } from '@components/operatorPanel/hooks/useManageHomePageButtonsPress';
import HomePageBottomPartButtons from '@components/operatorPanel/components/HomePage/HomePageBottomPartButtons';
import ModifyNCFileTypeButton from '@components/operatorPanel/components/elements/buttons/ModifyNCFileTypeButton';
import ViewNCFileButton from '@components/operatorPanel/components/elements/buttons/ViewNCFileButton';
import { PanelProps } from '@components/operatorPanel/components/panel';

export interface HomePageBottomPartProps
  extends Pick<PanelProps, 'CC' | 'selectedItem' | 'isOperator' | 'productionBatchId' | 'scale' | 'font'> {
  machineStatus: string;
  isWithERP: boolean | null;
  ncFileStatus: (typeof ncFileStatuses)[keyof typeof ncFileStatuses];
  ncFileId: number;
  ncFileName: string;
  autoOpenNCFileModal?: boolean;
  systemPopup?: any;
  shouldHidePopupParam?: boolean;
}

interface ImageSectionProps extends Pick<HomePageBottomPartProps, 'CC' | 'machineStatus' | 'scale'> {
  shouldDisplayImages: boolean;
  selectedMachineId: number;
}

const ImageSection: FC<ImageSectionProps> = ({ shouldDisplayImages, CC, machineStatus, scale, selectedMachineId }) => {
  if (!shouldDisplayImages) {
    return null;
  }

  return <HomePageImages CC={CC} machineStatus={machineStatus} scale={scale} selectedMachineId={selectedMachineId} />;
};

const HomePageBottomPartComponent: FC<HomePageBottomPartProps> = ({
  machineStatus,
  CC,
  isWithERP,
  ncFileStatus,
  ncFileId,
  ncFileName,
  selectedItem,
  productionBatchId,
  isOperator = true,
  scale = initialScale,
  font = initialFont,
  autoOpenNCFileModal = false,
  systemPopup,
  shouldHidePopupParam,
}) => {
  const { batchLinked, batchNotLinked, machineIsActive, ncFileChange, ncFileIgnored, ncFileLinked, ncFileUnknown, ncFileSetup } =
    useOperatorPanelStatuses({
      machineStatus,
      ncFileStatus,
      isOperator,
    });

  if (!ncFileName) return null;

  const shouldDisplayImages =
    (ncFileLinked && batchLinked && isOperator) ||
    (ncFileLinked && batchNotLinked && isOperator && !isWithERP) ||
    (ncFileUnknown && !isOperator && !isWithERP && machineIsActive) ||
    (ncFileSetup && isOperator);

  const shouldDisplayLinkToJobButton = ncFileLinked && batchNotLinked && isOperator && isWithERP;

  const shouldDisplayScheduledButton = typeof isWithERP === 'boolean' && isWithERP;

  const shouldDisplayNCFilesButton = typeof isWithERP === 'boolean' && !isWithERP;

  const shouldDisplayInfoButton = (ncFileLinked || ncFileChange || ncFileUnknown || ncFileSetup) && isOperator;

  const shouldDisplaySelectNCFileType = ncFileUnknown && isOperator;

  const shouldDisplayModifyViewNCFileButtons = (ncFileChange || ncFileIgnored) && isOperator;

  const shouldDisplayViewNCFileButton = ncFileIgnored && isOperator;

  const { onInformationPress, onLinkToJobPress, onModifyNCFileTypePress, onNCFilesPress, onScheduledPress, onViewNCFilePress } =
    useManageHomePageButtonsPress({ CC, ncFileId, ncFileName, font, productionBatchId, scale });

  if (shouldDisplayModifyViewNCFileButtons) {
    return (
      <View style={{ gap: scale(36), alignItems: 'center' }}>
        <ModifyNCFileTypeButton onPress={() => onModifyNCFileTypePress()} CC={CC} scale={scale} font={font} />
        {shouldDisplayViewNCFileButton && (
          <ViewNCFileButton onPress={() => onViewNCFilePress()} CC={CC} scale={scale} font={font} />
        )}
      </View>
    );
  }

  const shouldCenterElements = !shouldDisplayInfoButton && !shouldDisplayScheduledButton && !shouldDisplayNCFilesButton;

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: shouldCenterElements ? 'center' : 'space-between',
        width: '100%',
      }}
    >
      {(shouldDisplayInfoButton || shouldDisplayScheduledButton || shouldDisplayNCFilesButton) && (
        <OperatorPanelButton
          {...{
            onPress: onInformationPress,
            machineStatus,
            CC,
            iconConfig: { icon: 'infoFilled', color: CC('Label/Dark/AlwaysLight Primary'), width: scale(18), height: scale(17) },
            text: 'Information',
            buttonContainerStyle: {
              opacity: shouldDisplayInfoButton ? 1 : 0,
            },
            scale,
            font,
            testID: TEST_IDS.operatorPanel.home.informationButton,
          }}
        />
      )}

      {shouldDisplayImages ? (
        <ImageSection
          shouldDisplayImages={shouldDisplayImages}
          CC={CC}
          machineStatus={machineStatus}
          scale={scale}
          selectedMachineId={selectedItem.id}
        />
      ) : (
        <HomePageBottomPartButtons
          shouldDisplaySelectNCFileType={shouldDisplaySelectNCFileType}
          shouldDisplayLinkToJobButton={!!shouldDisplayLinkToJobButton}
          onModifyNCFileTypePress={onModifyNCFileTypePress}
          onLinkToJobPress={onLinkToJobPress}
          CC={CC}
          scale={scale}
          font={font}
          autoOpenNCFileModal={autoOpenNCFileModal}
          systemPopup={systemPopup}
          shouldHidePopupParam={shouldHidePopupParam}
        />
      )}

      {shouldDisplayScheduledButton && (
        <OperatorPanelButton
          {...{
            onPress: onScheduledPress,
            machineStatus,
            CC,
            iconConfig: { icon: 'clock', color: CC('Label/Dark/AlwaysLight Primary'), width: scale(18), height: scale(17) },
            text: 'Scheduled',
            scale,
            font,
            testID: TEST_IDS.operatorPanel.home.scheduledButton,
          }}
        />
      )}

      {shouldDisplayNCFilesButton && (
        <OperatorPanelButton
          {...{
            onPress: onNCFilesPress,
            machineStatus,
            CC,
            iconConfig: { icon: 'markdownIcon', color: CC('Label/Dark/AlwaysLight Primary'), width: scale(18), height: scale(17) },
            text: 'NC Files',
            scale,
            font,
          }}
        />
      )}
    </View>
  );
};

const HomePageBottomPart = connect(({ membership: { isWithERP } }) => ({ isWithERP }), {})(HomePageBottomPartComponent);

export default HomePageBottomPart;
